import {Fragment, useState} from "react";
import {twMerge} from "tailwind-merge";
import {Listbox} from "@headlessui/react";
import {ChevronUpDownIcon, MagnifyingGlassIcon} from "@heroicons/react/24/solid";
import {useApi} from "components/token";
import {fuzzySearch} from "components/fuzzy-search"

export function If({condition, children}) {
	return Boolean(condition) ? children : [];
}

function LoadingIcon({className, ...props}) {
	return (
		<svg {...props} className={twMerge(className, "animate-spin")} viewBox="0 0 100 101"
			 xmlns="http://www.w3.org/2000/svg">
			<path
				d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
				fill="#e2e8f0"/>
			<path
				d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
				fill="#475569"/>
		</svg>
	);
}

export function Loading({condition, children, ...props}) {
	return Boolean(condition) ? children : <LoadingIcon {...props}/>;
}

export function Button({className, children, ...props}) {
	return (
		<button {...props}
				className={twMerge("py-2 px-4 rounded shadow bg-slate-600 hover:bg-slate-700 text-white text-sm font-bold focus:outline-none", className)}>
			{children}
		</button>
	);
}

export function Textarea({className, ...props}) {
	return (
		<textarea maxLength="256" {...props}
				  className={twMerge("w-full focus:outline-none rounded border border-slate-300 p-3 overflow-y-hidden", className)}/>
	);
}

export function SearchBox({children, className, ...props}) {
	return (
		<div className={twMerge("flex flex-col gap-3", className)}>
			<div className="flex flex-row items-center gap-5">
				<div className="pointer-events-none flex items-center px-1">
					<MagnifyingGlassIcon className="size-5 text-slate-400"/>
				</div>
				<input type="text" {...props} className="bg-transparent focus:outline-none w-full"/>
			</div>
			{children}
		</div>
	);
}

export function byKeywords(value, callback) {
	let values = value.toLowerCase()
		.split(" ")
		.filter(v => v.trim());
	return function (item) {
		let keywords = callback(item);
		return values.every(v =>
			keywords.some(k => k.toLowerCase().includes(v)))
	}
}

function compareUserEmailAddress(u1, u2) {
	return u1.emailAddress.localeCompare(u2.emailAddress)
}

export function UserSearch({placeholder, group, limit, children, ...props}) {
	let [value, setValue] = useState("");

	let groups = group.split(" ");

	let users = groups.map(g => useApi("GET", "/group/" + g))
		.reduce((a, i) => [...a, ...i[0] ?? []], [])
	users.sort(compareUserEmailAddress);

	// let results = users.filter(byKeywords(value,
	// 	u => [u.emailAddress, u.firstname, u.lastname])).slice(0, limit);

	let results = fuzzySearch(value, users)

	return (
		<div className="flex flex-col gap-1 lg:gap-5">
			<SearchBox placeholder={placeholder} value={value} onChange={(e) => setValue(e.target.value)}
					   className="p-3 bg-white rounded shadow">
				<hr/>
				<span
					className="text-xs text-slate-500">You are seeing {results.length} of total {users.length} results</span>
			</SearchBox>
			<div {...props}>
				{results.map((u, i) => <Fragment key={i}>{children(u)}</Fragment>)}
			</div>
		</div>
	);
}

export function Select({value, onChange, placeholder, disabled, multiple, limit, search, children}) {
	let [searchValue, setSearch] = useState("");
	let values = value instanceof Array ? value : (value ?? []);
	return (
		<Listbox value={values} onChange={onChange}
				 disabled={disabled} multiple={multiple} as="div" className="relative">
			<Listbox.Button
				className={`p-3 w-full text-left ${disabled ? "bg-gray-100" : "bg-white"} border border-slate-300 rounded focus:outline-none flex flex-row gap-1 justify-between items-center`}>
                <span className="text-xs text-slate-600 word-wrap">
                    {
						values.length > 0 ?
							children.filter(c => values.includes(c.props.value))
								.map(c => c.props.children).join(', ') : placeholder
					}
                </span>
				<If condition={!disabled}>
					<ChevronUpDownIcon className="size-5 text-gray-400"/>
				</If>
			</Listbox.Button>
			<Listbox.Options
				className="absolute z-50 mt-1 max-h-64 w-full overflow-auto bg-white rounded shadow focus:outline-none">
				<If condition={search}>
					<SearchBox placeholder="Type here to search" className="p-3"
							   value={searchValue} onChange={(e) => setSearch(e.target.value)}
							   onKeyDown={(e) => (e.code === "Space") && e.stopPropagation()}/>
					{children.filter(byKeywords(searchValue, o => [o.props.children, o.props.value])).slice(0, limit)}
				</If>
				<If condition={!search}>
					{children}
				</If>
			</Listbox.Options>
		</Listbox>
	);
}

export function Option({value, children}) {
	return (
		<Listbox.Option value={value} className={({active}) =>
			`p-3 select-none text-xs text-slate-600 ${active ? "bg-slate-50" : "bg-white"}`}>
			{
				({selected}) =>
					<span className={`${selected ? "font-bold" : "font-normal"} truncate`}>{children}</span>
			}
		</Listbox.Option>
	);
}
